<template>
    <div>
        <provider-card-skeleton v-if="loading" />
        <div v-else-if="widgetReady">
            <div class="ac-floating-panel">
                <div class="ac-panel-content ac-white-panel provider-card">
                    <div class="provider-card-main">
                        <img :src="profileImage" alt="" class="provider-avatar">
                        <div class="provider-info">
                            <span class="provider-name">{{ vm.name }}</span>
                            <span class="provider-designation">
                                {{ vm.designation }}
                                <a v-if="vm.physicianEducation && (vm.physicianEducation.medicalSchool != null || vm.physicianEducation.residency != null)" href="#" 
                                class="wl-interactive" @click.prevent="viewEducation">(more&nbsp;info)</a>
                            </span>
                            <span class="provider-network">{{ vm.networkName }}</span>
                            <span v-if="vm.isProviderAcceptingNewPatients" class="provider-status accepting">
                                <i class="fa fa-thumbs-up"></i> Accepting New Members
                            </span>
                            <span v-else class="provider-status not-accepting">
                                <i class="fa fa-times-circle"></i> Not Accepting New Members
                            </span>
                        </div>
                        <div class="provider-contact">
                            <template v-if="hasAddressSetUp && !showVirtualPlans">
                                <a :href="mapUrl" target="_blank" class="contact-item address-link">
                                    <i class="fas fa-map-marker-alt contact-icon"></i>
                                    <span>
                                        {{ vm.address.address1 }}<span v-if="vm.address.address2">, {{ vm.address.address2 }}</span><br>
                                        {{ vm.address.city }}, {{ vm.address.stateName }} {{ vm.address.zipCode }}
                                    </span>
                                </a>
                            </template>
                            <span class="contact-item">
                                <i class="fas fa-phone contact-icon"></i>
                                <span>{{ vm.phoneNumber | phoneNumber }}</span>
                            </span>
                        </div>
                    </div>
                    <div v-if="vm.biography" class="provider-bio">
                        <template v-if="!readMore"><span v-html="shortBio"></span></template>
                        <template v-if="readMore"><span v-html="vm.biography"></span></template>
                        <a v-if="shortBio" class="wl-interactive bio-toggle" @click.prevent="readMoreToggle()">
                            Read {{ readMore ? "Less" : "More" }}
                        </a>
                    </div>
                </div>
            </div>
            <ac-modal :title="'About '+vm.name" ref="physicianInfoModal" :size-class="'modal-md'">
                <physician-info :provider-entity-id="providerEntityId" :showBio="false" :showOffice="true"></physician-info>
            </ac-modal>
        </div>
    </div>
</template>


<script>
    import {common} from "../../../global/common.js";
    import {api} from "../../../global/framework/api.js";
    import AcModal from "../../../global/customHtmlElements/AcModal.vue";
    import PhysicianInfo from "../physicianProfileAndPlans/PhysicianInfo.vue";
    import {mapGetters} from "vuex";
    import ProviderCardSkeleton from "./ProviderCardSkeleton.vue";

    export default {
        name: "ProviderProfile",
        components: {AcModal, PhysicianInfo, ProviderCardSkeleton},
        props: {
            providerEntityId: {type: [Number], required: true},
            showPanelTitle: {type: [Boolean], default: false},
        },
        data() {
            return {
                vm: {},
                loading: true,
                widgetReady: false,
                staticMapUrl: "https://maps.googleapis.com/maps/api/staticmap",
                readMore: false,
                shortBio: "",
                shortClinicBio: "",
                clinicBio: "",
                clinicReadMore: false
            };
        },
        methods: {
            readMoreToggle(isClinic) {
                if (isClinic) {
                    this.clinicReadMore = !this.clinicReadMore;
                } else {
                    this.readMore = !this.readMore;
                }
            },
            viewEducation() {
                this.$refs.physicianInfoModal.open();
            },
        },
        computed: {
            ...mapGetters({showVirtualPlans: 'globalSettings/showVirtualPlans'}),
            hasAddressSetUp() {
                return this.widgetReady && this.hasValue(this.vm.address) && this.hasValue(this.vm.address.addressId);
            },
            profileImage() {
                if (this.widgetReady) {
                    return common.generateImageLink(this.vm.profilePictureId, common.IMAGE_SIZE.MEDIUM.id);
                }
                return "";
            },
            mapUrl() {
                return this.widgetReady && this.hasAddressSetUp ?
                    `http://maps.google.com/?q=${this.vm.address.latitude},${this.vm.address.longitude}`
                    : '';
            },
            mapImageUrl() {
                if (!this.widgetReady || !this.hasAddressSetUp) {
                    return "";
                }
                let address = this.vm.address;
                let mapImagePath = common.appendUrlParameter(this.staticMapUrl, 'zoom', 12);
                mapImagePath = common.appendUrlParameter(mapImagePath, 'size', '200x200');
                mapImagePath = common.appendUrlParameter(mapImagePath, 'maptype', 'roadmap');
                mapImagePath = common.appendUrlParameter(mapImagePath, 'markers', 'color:red|' + address.latitude + ',' + address.longitude);
                mapImagePath = common.appendUrlParameter(mapImagePath, 'key', 'AIzaSyBULgN2oWltDQFqUfdnoAFSg76UcLTbvvM');
                return mapImagePath;
            },
        },
        async mounted() {
            try {
                const detailsUrl = `Shared/getProviderDetailsAndBio/${this.providerEntityId}`;
                const networkUrl = `Shared/GetProviderNetwork/${this.providerEntityId}`;
                const parentPromise = api.get("Shared", this.providerEntityId, "GetProviderParent").catch(() => null);
                const [data, providerName, parentInfo] = await Promise.all([
                    $.ajax({type: "Get", url: detailsUrl}),
                    $.ajax({type: "Get", url: networkUrl}),
                    parentPromise,
                ]);
                if (data.designation == "U") {
                    return;
                }
                this.clinicBio = parentInfo && parentInfo.about != null ? parentInfo.about : "";
                this.mapData('vm', data);
                this.vm.networkName = providerName ? providerName.name : "";
                this.vm.designation = common.filterProfDesignations(this.vm.designation);
                this.shortBio = common.readMoreSplit(300, this.vm.biography);
                this.readMore = !this.shortBio;
                this.shortClinicBio = common.readMoreSplit(200, this.clinicBio);
                this.clinicReadMore = this.shortClinicBio ? false : true;
                this.widgetReady = true;
            } finally {
                this.loading = false;
                this.$emit("profile-ready");
            }
        },
    }
</script>

<style scoped>
    .ac-floating-panel {
        margin-top: 10px;
    }

    .provider-card {
        padding: 24px;
    }

    .provider-card-main {
        display: flex;
        align-items: flex-start;
        gap: 20px;
    }

    .provider-avatar {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        object-fit: cover;
        flex-shrink: 0;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
    }

    .provider-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
    }

    .provider-name {
        font-size: 1.4rem;
        font-weight: 700;
        color: #294054 !important;
        display: block;
        line-height: 1.3;
    }

    .provider-designation {
        font-size: 1rem;
        color: #5f6368 !important;
        display: block;
    }

    .provider-network {
        font-size: 0.9rem;
        color: #989392 !important;
        display: block;
    }

    .provider-status {
        font-size: 0.85rem;
        display: inline-flex;
        align-items: center;
        gap: 5px;
        margin-top: 6px;
        padding: 3px 10px;
        border-radius: 999px;
        width: fit-content;
    }

    .provider-status.accepting {
        color: #198754 !important;
        background: #d1e7dd;
    }

    .provider-status.accepting i {
        color: #198754 !important;
    }

    .provider-status.not-accepting {
        color: #842029 !important;
        background: #f8d7da;
    }

    .provider-status.not-accepting i {
        color: #842029 !important;
    }

    .provider-contact {
        display: flex;
        flex-direction: column;
        gap: 10px;
        flex-shrink: 0;
        text-align: right;
        font-size: 0.9rem;
    }

    .contact-item {
        display: flex;
        align-items: flex-start;
        gap: 8px;
    }

    .contact-icon {
        margin-top: 3px;
        flex-shrink: 0;
    }

    .address-link {
        text-decoration: none;
        color: inherit !important;
    }

    .address-link:hover span {
        text-decoration: underline;
    }

    .provider-bio {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid #e9ecef;
        overflow-wrap: break-word;
    }

    .bio-toggle {
        font-size: 0.9rem;
    }

    @media (max-width: 768px) {
        .provider-card-main {
            flex-direction: column;
            align-items: center;
            text-align: center;
        }

        .provider-info {
            align-items: center;
        }

        .provider-status {
            margin-left: auto;
            margin-right: auto;
        }

        .provider-contact {
            text-align: center;
            align-items: center;
        }
    }
</style>
